import React, { useState, useEffect, useMemo } from "react";
import type { Meeting } from "../hooks/useMeetingHistory";
import { useUiLang } from "../../../context/UiLangContext";
import { useMeetingHistory } from "../hooks/useMeetingHistory";
import { useFolders } from "../hooks/useFolders";
import { HistoryFilters } from "./HistoryFilters";
import { FolderTreeView } from "./FolderTreeView";

interface TranslationHistoryProps {
  translatorServiceUrl: string;
  recording: boolean;
  currentMeetingId?: string | null;
  onOpenSavedSummary: (meeting: Meeting, summaryText: string, htmlFilename?: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

export const TranslationHistory: React.FC<TranslationHistoryProps> = ({
  translatorServiceUrl,
  recording,
  currentMeetingId = null,
  onOpenSavedSummary,
  isOpen = true,
  onClose,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  const {
    meetings: pastMeetings,
    loading,
    loadingMeetingId,
    loadMeetingSummary,
    fetchMeetings,
    moveMeetingToFolder,
    updateMeetingTags,
    deleteMeeting,
    error,
    setError,
  } = useMeetingHistory(translatorServiceUrl);
  const [movingMeetingId, setMovingMeetingId] = useState<string | number | null>(null);

  const {
    folders,
    loading: foldersLoading,
    loadFolders,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
  } = useFolders(translatorServiceUrl);

  // States
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<string>("");

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Extract all unique tags from user's meetings dynamically
  const uniqueTags = useMemo(() => {
    const allTags = new Set<string>();
    pastMeetings.forEach((m) => {
      if (m.tags && Array.isArray(m.tags)) {
        m.tags.forEach((tag) => {
          if (tag && typeof tag === "string") {
            allTags.add(tag.trim());
          }
        });
      }
    });
    return Array.from(allTags).sort();
  }, [pastMeetings]);

  // Initial load + refetch on filters change
  useEffect(() => {
    fetchMeetings(debouncedSearchQuery, { tags: selectedTag, isEn });
    loadFolders();
  }, [fetchMeetings, loadFolders, debouncedSearchQuery, selectedTag, isEn]);

  // Listen to meetings-changed event
  useEffect(() => {
    const onMeetingsChanged = () =>
      fetchMeetings(debouncedSearchQuery, { tags: selectedTag, isEn, silent: true });
    window.addEventListener("Cora-Lingo:meetings-changed", onMeetingsChanged);
    return () => window.removeEventListener("Cora-Lingo:meetings-changed", onMeetingsChanged);
  }, [fetchMeetings, debouncedSearchQuery, selectedTag, isEn]);

  const prevRecordingRef = React.useRef(recording);
  useEffect(() => {
    if (prevRecordingRef.current && !recording) {
      setTimeout(() => {
        fetchMeetings(debouncedSearchQuery, { tags: selectedTag, isEn });
      }, 1000);
    }
    prevRecordingRef.current = recording;
  }, [recording, fetchMeetings, debouncedSearchQuery, selectedTag, isEn]);

  // Filter out the currently active meeting if recording
  const displayedMeetings = pastMeetings.filter((meeting) => {
    if (recording && currentMeetingId && meeting._id.toString() === currentMeetingId.toString()) {
      return false;
    }
    return true;
  });

  const handleLoad = async (meeting: Meeting) => {
    await loadMeetingSummary(meeting, recording, isEn, onOpenSavedSummary);
    if (onClose) {
      onClose();
    }
  };

  const handleMoveMeeting = async (meetingId: string | number, folderId: string | number | null) => {
    setMovingMeetingId(meetingId);
    try {
      const success = await moveMeetingToFolder(meetingId, folderId, isEn);
      if (success) {
        fetchMeetings(debouncedSearchQuery, { tags: selectedTag, isEn, silent: true });
      }
    } finally {
      setMovingMeetingId(null);
    }
  };

  const handleUpdateTags = async (meetingId: string | number, tags: string[]) => {
    await updateMeetingTags(meetingId, tags, isEn);
  };

  const formatDate = (isoString: string) => {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const timeStr = d.toLocaleTimeString(isEn ? "en-US" : "es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });

    if (targetDate.getTime() === today.getTime()) {
      return `${isEn ? "Today" : "Hoy"}, ${timeStr}`;
    } else if (targetDate.getTime() === yesterday.getTime()) {
      return `${isEn ? "Yesterday" : "Ayer"}, ${timeStr}`;
    } else {
      const dateStr = d.toLocaleDateString(isEn ? "en-US" : "es-ES", {
        day: "numeric",
        month: "short"
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  const formatDuration = (meeting: Meeting) => {
    const durVal = meeting.duration ?? meeting.duration_seconds ?? meeting.duration_ms ?? meeting.dur ?? meeting.segs ?? meeting.length;
    if (durVal !== undefined && durVal !== null && durVal !== "") {
      if (typeof durVal === "string" && (durVal.includes("min") || durVal.includes("seg") || durVal.includes("sec"))) {
        return durVal;
      }
      if (typeof durVal === "string" && durVal.includes(":")) {
        const parts = durVal.split(":");
        if (parts.length === 2) {
          const minsStr = (parts[0] || "00").padStart(2, "0");
          const secsStr = (parts[1] || "00").padStart(2, "0");
          return `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
        }
        if (parts.length === 3) {
          const hrsStr = (parts[0] || "00").padStart(2, "0");
          const minsStr = (parts[1] || "00").padStart(2, "0");
          const secsStr = (parts[2] || "00").padStart(2, "0");
          return parseInt(hrsStr) > 0
            ? `${hrsStr} h ${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`
            : `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
        }
      }
      const numSec = Number(durVal);
      if (!isNaN(numSec) && numSec >= 0) {
        const totalSecs = numSec > 50000 ? Math.floor(numSec / 1000) : numSec;
        const minsStr = Math.floor(totalSecs / 60).toString().padStart(2, "0");
        const secsStr = Math.floor(totalSecs % 60).toString().padStart(2, "0");
        return `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
      }
    }
    return `00 min ${isEn ? "and" : "y"} 00 seg`;
  };

  return (
    <>
      {/* Backdrop for Mobile */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[40] transition-opacity duration-300" 
          onClick={onClose} 
        />
      )}

      {/* Drawer Container */}
      <div className={`
        flex-shrink-0 flex-col bg-[var(--bg2)] border-b lg:border-b-0 lg:border-r border-[var(--b1)] p-3.5 pt-2.5 overflow-hidden transition-transform duration-300 w-[340px]
        fixed top-0 left-0 h-full z-[50] shadow-2xl
        lg:relative lg:z-auto lg:h-auto lg:shadow-none lg:order-1
        ${isOpen ? "translate-x-0 flex" : "-translate-x-full lg:translate-x-0 flex"}
      `}>
      
      {/* Mobile Close Button */}
      <div className="lg:hidden flex justify-between items-center mb-2 px-1">
        <span className="font-bold text-xs text-[var(--text)] tracking-wider uppercase">{isEn ? "History" : "Historial"}</span>
        <button onClick={onClose} className="p-1 rounded-full bg-[var(--b1)] text-[var(--text-dim)] hover:text-[var(--text)] transition-colors">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </div>

      {/* ── SEARCH & TAGS FILTERS ── */}
      <HistoryFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedTag={selectedTag}
        setSelectedTag={setSelectedTag}
        uniqueTags={uniqueTags}
        isEn={isEn}
      />

      {error && (
        <div className="mb-2.5 p-2.5 rounded-[8px] bg-[var(--danger)]/10 border border-[var(--danger)]/25 text-xs text-[var(--danger)] flex items-start gap-1.5 flex-shrink-0 transition-all">
          <span className="font-bold flex-shrink-0 mt-0.5">⚠</span>
          <p className="leading-tight flex-1 font-medium">{error}</p>
          <button onClick={() => setError("")} className="text-[var(--danger)] hover:opacity-75 font-bold cursor-pointer ml-auto">✕</button>
        </div>
      )}

      {/* ── FOLDER TREE VIEW ── */}
      <FolderTreeView
        folders={folders}
        meetings={displayedMeetings}
        loading={loading || foldersLoading}
        loadingMeetingId={loadingMeetingId}
        movingMeetingId={movingMeetingId}
        isEn={isEn}
        onLoadMeeting={handleLoad}
        onMoveMeeting={handleMoveMeeting}
        onUpdateTags={handleUpdateTags}
        onDeleteMeeting={async (id) => {
          await deleteMeeting(id, isEn);
        }}
        onCreateFolder={handleCreateFolder}
        onRenameFolder={handleRenameFolder}
        onDeleteFolder={handleDeleteFolder}
        formatDate={formatDate}
        formatDuration={formatDuration}
      />
      </div>
    </>
  );
};
